import { Alert, Button, Skeleton, Tag, theme } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import {
    MdOutlineAutoDelete,
    MdOutlineCleaningServices,
    MdOutlineDns,
    MdOutlineInsertDriveFile,
    MdOutlinePersonOff,
    MdOutlineStorage,
} from 'react-icons/md'
import { TbFolderCog } from 'react-icons/tb'
import { Link } from 'react-router-dom'

import { withDiskFeature, formatBytes, pluralUk } from '@/Containers/DiskSection/DiskContainer/Hooks'
import { Panel, formatInt, shownOf, useChartColors } from '@/Ship/Components/Dashboard/Overview/shared'
import { Hint } from '@/Ship/Components/Hint'
import { classNames } from '@/Ship/Helpers/classNames'

import { CategoryStack, StateDonut } from '../Components/DiskAdminCharts'
import { DISK_ADMIN_TITLE, DiskAdminShell } from '../Components/DiskAdminShell'
import { StorageName } from '../Components/StorageName'
import { UsageTrend } from '../Components/UsageTrend'
import { storageGroups } from '../Helpers/diskAdminCharts'
import { formatDate, formatDateTime } from '../Helpers/diskAdminFormat'
import { diskAdminStorageUrl, diskAdminStoragesUrl } from '../Helpers/diskAdminRoutes'
import { diskAdminOverviewStore as store } from '../Stores/DiskAdminOverviewStore'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'
import type { CSSProperties, ReactNode } from 'react'

import overview from '@/Ship/Components/Dashboard/Overview/Overview.module.css'
import styles from '../Components/DiskAdmin.module.css'

interface KpiTileProps {
    id: string
    title: string
    icon: ReactNode
    accent: string
    value: ReactNode
    note: ReactNode
    to?: string
}

/** Плитка показника — та сама геометрія, що на «Робочому столі»; підпис із трикрапкою, повний — у `title`. */
const KpiTile = ({ id, title, icon, accent, value, note, to }: KpiTileProps) => {
    const { token } = theme.useToken()
    const body = (
        <div className={classNames(overview['kpi'])} style={{ '--kpi-accent': accent } as CSSProperties} data-kpi={id}>
            <div className={classNames(overview['kpiLabel'])} title={title}>
                <span className={classNames(overview['kpiIcon'])} style={{ color: accent, background: token.colorFillTertiary }}>
                    {icon}
                </span>
                {title}
            </div>
            <div className={classNames(overview['kpiValue'])} data-kpi-value>
                {value}
            </div>
            <div className={classNames(styles['kpiNote'])}>{note}</div>
        </div>
    )

    return to ? (
        <Link to={to} className={classNames(styles['kpiLink'])}>
            {body}
        </Link>
    ) : (
        body
    )
}

const KpiRow = observer(() => {
    const { token } = theme.useToken()
    const chart = useChartColors()
    const data = store.overview

    if (!data) {
        return (
            <div className={classNames(styles['kpis'])} data-block="disk-admin-kpi">
                {Array.from({ length: 6 }, (_, index) => (
                    <div key={index} className={classNames(overview['kpi'])}>
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                    </div>
                ))}
            </div>
        )
    }

    const { tenant, reclaimable, offboarding } = data
    const reclaim = reclaimable.oldVersions + reclaimable.trashOlderThan + reclaimable.duplicates.bytes

    return (
        <div className={classNames(styles['kpis'])} data-block="disk-admin-kpi">
            <KpiTile
                id="used"
                title="Зайнято"
                icon={<MdOutlineStorage />}
                accent={chart.primary}
                value={formatBytes(tenant.used + tenant.reserved)}
                note={
                    tenant.quota !== null
                        ? `з ${formatBytes(tenant.quota)} · ${Math.round(tenant.percent ?? 0)} %`
                        : 'ліміт компанії не задано'
                }
            />
            <KpiTile
                id="files"
                title="Файлів"
                icon={<MdOutlineInsertDriveFile />}
                accent={chart.third}
                value={formatInt(tenant.files)}
                note={`${formatInt(tenant.folders)} ${pluralUk(tenant.folders, 'папка', 'папки', 'папок')} · ${formatInt(tenant.versions)} ${pluralUk(tenant.versions, 'версія', 'версії', 'версій')}`}
            />
            <KpiTile
                id="physical"
                title="На сервері"
                icon={<MdOutlineDns />}
                accent={chart.fourth}
                value={formatBytes(tenant.physical)}
                note={`логічно ${formatBytes(tenant.logical)} · дедуплікація −${formatBytes(tenant.dedupSaved)}`}
            />
            <KpiTile
                id="reclaimable"
                title="Можна звільнити"
                icon={<MdOutlineCleaningServices />}
                accent={chart.fifth}
                value={formatBytes(reclaim)}
                note="старі версії, давній кошик, дублікати"
            />
            <KpiTile
                id="pending-gc"
                title="Чекає прибирання"
                icon={<MdOutlineAutoDelete />}
                accent={chart.second}
                value={formatBytes(tenant.pendingGc)}
                note={`${formatInt(tenant.pendingGcBlobs)} ${pluralUk(tenant.pendingGcBlobs, 'файл', 'файли', 'файлів')} на сервері · до ${tenant.pendingGcDays} дн.`}
            />
            <KpiTile
                id="offboarding"
                title="Справи звільнених"
                icon={<MdOutlinePersonOff />}
                accent={offboarding.overdue > 0 ? token.colorError : chart.sixth}
                value={formatInt(offboarding.pending)}
                note={
                    offboarding.overdue > 0 ? (
                        <Tag color="red" style={{ margin: 0 }}>
                            прострочено {formatInt(offboarding.overdue)}
                        </Tag>
                    ) : offboarding.pending > 0 ? (
                        'чекають рішення'
                    ) : (
                        'немає відкритих справ'
                    )
                }
                to={diskAdminStoragesUrl('departed')}
            />
        </div>
    )
})

const StorageGroupsPanel = observer(() => {
    const chart = useChartColors()
    const rows = store.overview ? storageGroups(store.overview.byStorageType) : []

    return (
        <Panel
            block="disk-admin-by-type"
            title="За просторами"
            subtitle="Зайнято разом із кошиком і старими версіями"
            loading={store.isLoading && !store.overview}
            skeletonRows={6}
        >
            <div className={classNames(overview['bars'])}>
                {rows.map((row) => (
                    <div key={row.type} className={classNames(overview['barRow'])} data-storage-group={row.type}>
                        <span className={classNames(overview['barLabel'])} title={row.label}>
                            {row.label}
                            <span className={classNames(overview['barMuted'])}>
                                {formatInt(row.storages)} {pluralUk(row.storages, 'простір', 'простори', 'просторів')}
                            </span>
                        </span>
                        <span className={classNames(overview['barValue'])}>{formatBytes(row.used + row.reserved)}</span>
                        <div className={classNames(overview['track'])}>
                            <div
                                className={classNames(overview['fill'])}
                                style={{ width: `${row.share}%`, background: chart.third }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </Panel>
    )
})

const TopPanel = observer(() => {
    const chart = useChartColors()
    const top = store.overview?.top
    const items = top?.items ?? []
    const max = Math.max(1, ...items.map((item) => item.used))

    return (
        <Panel
            block="disk-admin-top"
            title="Найбільші диски"
            subtitle="Особисті диски й простори за зайнятим місцем"
            extra={top ? shownOf(items.length, top.total, pluralUk(top.total, 'диска', 'дисків', 'дисків')) : null}
            loading={store.isLoading && !store.overview}
            empty={top && items.length === 0 ? 'На диску компанії ще немає файлів' : false}
            skeletonRows={10}
        >
            <div className={classNames(overview['bars'])}>
                {items.map((item, index) => (
                    <Link
                        key={item.uuid}
                        to={diskAdminStorageUrl(item.uuid)}
                        className={classNames(styles['rowLink'])}
                        data-top={item.uuid}
                    >
                        <div className={classNames(overview['barRow'])}>
                            <span className={classNames(overview['leader'])}>
                                <span className={classNames(overview['rank'], index < 3 ? overview['rankTop'] : undefined)}>
                                    {index + 1}
                                </span>
                                <StorageName type={item.type} title={item.title} state={item.state} owner={item.owner} size={24} />
                            </span>
                            <span className={classNames(overview['barValue'])}>
                                {formatBytes(item.used)}
                                {item.quota !== null ? (
                                    <span className={classNames(overview['barMuted'])}>з {formatBytes(item.quota)}</span>
                                ) : null}
                            </span>
                            <div className={classNames(overview['track'])}>
                                <div
                                    className={classNames(overview['fill'])}
                                    style={{ width: `${(item.used / max) * 100}%`, background: chart.primary }}
                                />
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
            {top && top.total > items.length ? (
                <div className={classNames(overview['footnote'])}>
                    <Link to={diskAdminStoragesUrl()}>Усі диски</Link>
                </div>
            ) : null}
        </Panel>
    )
})

const ReclaimPanel = observer(() => {
    const data = store.overview

    return (
        <Panel
            block="disk-admin-reclaim"
            title="Що можна звільнити"
            subtitle="Квоту звільняє остаточне видалення"
            loading={store.isLoading && !data}
            skeletonRows={6}
        >
            {data ? (
                <div className={classNames(styles['reclaim'])}>
                    <div className={classNames(styles['reclaimRow'])} data-reclaim="old-versions">
                        <span className={classNames(styles['reclaimTitle'])}>Старі версії файлів</span>
                        <span className={classNames(styles['reclaimValue'])}>{formatBytes(data.reclaimable.oldVersions)}</span>
                        <span className={classNames(styles['reclaimNote'])}>
                            {formatInt(data.reclaimable.oldVersionsCount)}{' '}
                            {pluralUk(data.reclaimable.oldVersionsCount, 'версія', 'версії', 'версій')} ·{' '}
                            <Link to={`${diskAdminStoragesUrl()}?sort=oldVersions`}>диски з найбільшими</Link>
                        </span>
                    </div>
                    <div className={classNames(styles['reclaimRow'])} data-reclaim="trash">
                        <span className={classNames(styles['reclaimTitle'])}>
                            Кошик старший за {data.reclaimable.trashOlderThanDays} дн.
                        </span>
                        <span className={classNames(styles['reclaimValue'])}>{formatBytes(data.reclaimable.trashOlderThan)}</span>
                        <span className={classNames(styles['reclaimNote'])}>
                            весь кошик — {formatBytes(data.byState.trash)} ·{' '}
                            <Link to={`${diskAdminStoragesUrl()}?sort=trash`}>диски з найбільшим</Link>
                        </span>
                    </div>
                    <div className={classNames(styles['reclaimRow'])} data-reclaim="duplicates">
                        <span className={classNames(styles['reclaimTitle'])}>Дублікати у спільних просторах</span>
                        <span className={classNames(styles['reclaimValue'])}>
                            {formatBytes(data.reclaimable.duplicates.bytes)}
                        </span>
                        <span className={classNames(styles['reclaimNote'])}>
                            {data.reclaimable.duplicates.groups > 0
                                ? `${formatInt(data.reclaimable.duplicates.files)} файлів у ${formatInt(data.reclaimable.duplicates.groups)} ${pluralUk(data.reclaimable.duplicates.groups, 'групі', 'групах', 'групах')} однакових`
                                : 'Однакових файлів у спільних просторах немає'}
                        </span>
                    </div>
                </div>
            ) : null}
            {data ? (
                <div style={{ marginTop: 12 }}>
                    <Hint id="disk.admin.physical">
                        Квоту звільняє остаточне видалення — з кошика чи старої версії. Місце на сервері звільняється
                        пізніше, через {data.tenant.pendingGcDays} дн., і лише якщо такого самого файлу немає в
                        інших місцях компанії: однаковий вміст зберігається один раз.
                    </Hint>
                </div>
            ) : null}
        </Panel>
    )
})

const OffboardingPanel = observer(() => {
    const counts = store.overview?.offboarding

    return (
        <Panel
            block="disk-admin-offboardings"
            title="Справи звільнених"
            subtitle="Диски деактивованих співробітників, що чекають рішення"
            extra={
                counts ? (
                    <span>
                        {formatInt(counts.pending)} чекають
                        {counts.overdue > 0 ? (
                            <Tag color="red" style={{ marginLeft: 8, marginRight: 0 }}>
                                прострочено {formatInt(counts.overdue)}
                            </Tag>
                        ) : null}
                    </span>
                ) : null
            }
            loading={store.isLoading && !store.overview}
            empty={store.overview && store.offboardings.length === 0 ? 'Немає дисків, що чекають рішення' : false}
            skeletonRows={4}
        >
            <div className={classNames(overview['bars'])}>
                {store.offboardings.map((row) => (
                    <Link
                        key={row.uuid}
                        to={row.storage ? diskAdminStorageUrl(row.storage.uuid) : diskAdminStoragesUrl('departed')}
                        className={classNames(styles['rowLink'])}
                        data-offboarding={row.uuid}
                    >
                        <div className={classNames(overview['barRow'])}>
                            <StorageName
                                type="personal"
                                title={row.user?.name ?? row.storage?.title ?? '—'}
                                state="frozen"
                                owner={row.user}
                                size={24}
                            />
                            <span className={classNames(overview['barValue'])}>
                                до {formatDate(row.dueAt)}
                                {row.overdue ? (
                                    <Tag color="red" style={{ marginLeft: 8, marginRight: 0 }}>
                                        прострочено
                                    </Tag>
                                ) : null}
                            </span>
                        </div>
                    </Link>
                ))}
            </div>
            {store.offboardingsTotal > store.offboardings.length ? (
                <div className={classNames(overview['footnote'])}>
                    {shownOf(store.offboardings.length, store.offboardingsTotal, 'справ')} ·{' '}
                    <Link to={diskAdminStoragesUrl('departed')}>усі звільнені</Link>
                </div>
            ) : null}
        </Panel>
    )
})

/**
 * Огляд компанії `/disk/admin` (`DISK-006` §3.1, §5.2, наряд Ф2): показники,
 * «Чим зайнято» складеною смугою, кільце стану місця, розподіл за
 * просторами, тренд, найбільші диски, що можна звільнити, справи звільнених.
 *
 * Числа — живі агрегати беку (`GET /services/disk/admin/overview`); тренд і
 * справи — окремими запитами, помилка одного не гасить решту.
 */
const IndexPage = observer(() => {
    useEffect(() => {
        store.load()
    }, [])

    const data = store.overview

    return (
        <DiskAdminShell
            tab="overview"
            documentTitle={DISK_ADMIN_TITLE}
            eyebrow={data?.generatedAt ? `Дані на ${formatDateTime(data.generatedAt)}` : 'Огляд диска компанії'}
        >
            {store.error !== null ? (
                <Alert
                    type="error"
                    showIcon
                    title={store.error}
                    action={
                        <Button size="small" onClick={() => void store.loadOverview()}>
                            Повторити
                        </Button>
                    }
                />
            ) : null}

            <KpiRow />

            <Panel
                block="disk-admin-categories"
                title="Чим зайнято місце"
                subtitle="Поточні версії файлів поза кошиком, за типами"
                extra={data ? formatBytes(data.byCategory.reduce((sum, row) => sum + row.bytes, 0)) : null}
                loading={store.isLoading && !data}
                skeletonRows={3}
            >
                {data ? <CategoryStack rows={data.byCategory} /> : null}
            </Panel>

            <div className={classNames(styles['grid3'])}>
                <Panel
                    block="disk-admin-state"
                    title="Стан місця"
                    subtitle="З чого складається зайняте"
                    loading={store.isLoading && !data}
                    skeletonRows={6}
                >
                    {data ? <StateDonut byState={data.byState} /> : null}
                </Panel>
                <StorageGroupsPanel />
                <Panel
                    block="disk-admin-trend"
                    title="Тренд зайнятого"
                    subtitle="Нічні знімки й поточний стан"
                    loading={store.isTrendLoading && !store.trend}
                    empty={store.trendError ?? false}
                    skeletonRows={6}
                >
                    {store.trend ? (
                        <UsageTrend
                            trend={store.trend}
                            days={store.trendDays}
                            onDaysChange={(days) => store.setTrendDays(days)}
                        />
                    ) : null}
                </Panel>
            </div>

            <div className={classNames(styles['grid2'])}>
                <TopPanel />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
                    <ReclaimPanel />
                    <OffboardingPanel />
                </div>
            </div>
        </DiskAdminShell>
    )
})

export const pageConfig: IPageConfig = {
    key: 'Disk.Admin',
    route: '/disk/admin',
    title: DISK_ADMIN_TITLE,
    icon: <TbFolderCog />,
}

export default withDiskFeature(IndexPage)
